/**
 * InstallComplete.jsx
 * Salesforce Remote Adapter Connector Component Node Implementation
 */

import React, { useState, useEffect } from 'react';
import './InstallComplete.css';
import { api, recordAuditLog, downloadFile } from '../../api/rapid-imlementation-workflow-api';
import { currentRole, isTabAllowed, CHROME_TABS } from './roleAccess';

export default function InstallComplete({ onTabChange }) {
  const role = currentRole();
  // Synchronization Framework States
  const [updateStatus, setUpdateStatus] = useState('processing'); // 'processing' | 'success' | 'failed'
  const [errorMessage, setErrorMessage] = useState('');
  const [ticketData, setTicketData] = useState({ url: '#', label: '' });

  // Read context parameters directly from active layout window parameters history tracking bounds
  const params = new URLSearchParams(window.location.search);
  const quoteNum = params.get("quoteNum") || "";
  const siteId = params.get("siteId") || "";
  const intakeId = params.get("intakeId") || "";
  const bundleUrl = quoteNum && siteId ? api.getChecklistPdfBundleUrl(quoteNum, siteId, intakeId) : '';

  useEffect(() => {
    recordAuditLog({
      category: 'checklist',
      action: 'Viewed Install Complete page',
      detail: `Salesforce update + checklist bundle for site ${siteId}.`,
      target: 'Install Complete',
      quoteNum,
      intakeId,
      siteId,
    }).catch(() => {});

    const executionStartTime = Date.now();

    api.triggerSalesforceUpdate(quoteNum, siteId, intakeId)
      .then(res => {
        // Enforces UI minimum processing buffer requirement to prevent flash shifts
        const calculatedDwellTime = Math.max(0, 4000 - (Date.now() - executionStartTime));
        
        setTimeout(() => {
          setTicketData({
            url: res.ticketUrl || "#",
            label: res.ticketLabel || "Salesforce operational instance ticket"
          });
          setUpdateStatus('success');
        }, calculatedDwellTime);
      })
      .catch(err => {
        setErrorMessage(err.message || 'Unknown integration tracking connection exception abort trace.');
        setUpdateStatus('failed');
      });
  }, [quoteNum, siteId, intakeId]);

  return (
    <div className="complete-wrapper">
      {/* LOCAL WORKSPACE INTEGRATED NAVIGATION CHROME */}
      <div id="chrome">
        <div className="chrome-logo"><span>SF</span> Sales Cloud</div>
        <div className="chrome-nav">
          {CHROME_TABS.filter(t => isTabAllowed(role, t.id)).map(t => (
            <button key={t.id} type="button" className={`chrome-nav-item ${t.id === 'checklist' ? 'active' : ''}`} onClick={() => onTabChange(t.id)}>{t.label}</button>
          ))}
        </div>
        <div className="chrome-crumb"><span>Dashboard</span><span>/</span><b>Salesforce Update</b></div>
      </div>

      <main className="shell">
        <section className="card">
          <div className="card-hd">
            <h1>
              {updateStatus === 'processing' && "Updating record in Salesforce, please wait ..."}
              {updateStatus === 'success' && "Update Successful"}
              {updateStatus === 'failed' && "Salesforce Dynamic Update Aborted"}
            </h1>
            <p>
              {updateStatus === 'processing' && "The checklist was verified and the generated PDF bundle has started downloading."}
              {updateStatus === 'success' && "The local Salesforce adapter returned a successful update response."}
              {updateStatus === 'failed' && "The system encountered processing restrictions changing parameters records inside data vaults."}
            </p>
          </div>
          
          <div className="card-bd">
            {updateStatus === 'processing' && (
              <div className="alert blue">Updating record in Salesforce, please wait ...</div>
            )}

            {updateStatus === 'failed' && (
              <>
                <div className="alert red">Salesforce update failed: {errorMessage}</div>
                <button type="button" className="btn" onClick={() => onTabChange('checklist')}>
                  Back to checklist tracking grid
                </button>
              </>
            )}

            {updateStatus === 'success' && (
              <>
                <div className="alert green">
                  <strong>Update Successful</strong><br />
                  Checklist approval has been recorded across destination production environments.
                </div>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '6px' }}>
                  {bundleUrl && (
                    <button type="button" className="btn green" onClick={() => downloadFile(bundleUrl, `${siteId}-checklist-bundle.zip`)}>
                      Download checklist bundle (Pre / Post / Delta)
                    </button>
                  )}
                  <a className="btn primary" href={ticketData.url} target="_blank" rel="noopener noreferrer">
                    Open Salesforce ticket: {ticketData.label}
                  </a>
                  <button type="button" className="btn" onClick={() => onTabChange('checklist')}>
                    Back to checklist
                  </button>
                </div>
              </>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}